import React from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import MenuItem from "@material-ui/core/MenuItem";
import { Link } from "react-router-dom";
import styled from "styled-components";
import differenceInYears from "date-fns/differenceInYears";

import { updateAccount } from "../../configureStore/user";
import {
  colors,
  GridWrapper,
  SmallFields,
  TextField,
  Select,
  BirthdayPicker,
  Button,
} from "../../theme";
import {
  SupportContainer,
  SupportContent,
  SupportMain,
  SupportAside,
} from "./supportTheme";
import Address from "../AddressField";
import SubscriptionOffer from "./SupportSubscriptionOffer";
import SocioOffer from "./SupportSocioOffer";
import FAQ from "./SupportFAQ";
import OverlayLoader from "../loader/OverlayLoader";

const SwitchPath = styled.div`
  background: ${colors.lightGray};
  padding: 20px;

  p {
    margin-bottom: 1.5rem;
  }

  a {
    color: ${colors.purple};
    font-weight: 600;
  }
`;

function SupportInformations({ dispatch, location, match, account }) {
  const { errors, handleSubmit, setValue, control, getValues } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      street: "",
      postal_code: "",
      locality: "",
      country_name: "",
      gender: "",
      birthday: null,
      birth_locality: "",
      birth_country_name: "",
      phone: "",
      ...account.address_billing,
      ...Object.keys(account).reduce((acc, key) => {
        if (account[key] !== null) {
          acc[key] = account[key];
        }
        return acc;
      }, {}),
      ...location.state,
    },
  });

  const isSocioPath = match.params.path === "socio";

  function onSubmit(data) {
    dispatch(
      updateAccount({
        ...data,
        address_billing: {
          street: data.street,
          postal_code: data.postal_code,
          locality: data.locality,
          country_name: data.country_name,
        },
      })
    );
  }

  return (
    <SupportContainer>
      <h1>
        <span>2.</span> Coordonnées
      </h1>
      <p>
        Nous avons besoin de vos coordonnées et de votre état civil pour pouvoir
        éditer vos parts sociales et vous permettre de devenir sociétaire de
        notre coopérative.
      </p>
      <SupportContent>
        <SupportMain>
          <OverlayLoader
            isVisible={account.isFetching}
            error={account.error != null}
          />
          <h2>Je renseigne mes coordonnées</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SmallFields>
              <fieldset>
                <Controller
                  as={TextField}
                  label="Prénom"
                  name="firstname"
                  control={control}
                  required
                  rules={{
                    required: "Le prénom est requis.",
                  }}
                  error={errors.firstname !== undefined}
                  helperText={errors.firstname && errors.firstname.message}
                />
              </fieldset>
              <fieldset>
                <Controller
                  as={TextField}
                  label="Nom"
                  name="lastname"
                  control={control}
                  required
                  rules={{
                    required: "Le nom est requis.",
                  }}
                  error={errors.lastname !== undefined}
                  helperText={errors.lastname && errors.lastname.message}
                />
              </fieldset>
            </SmallFields>

            <Address
              control={control}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
            />

            {!isSocioPath && (
              <SwitchPath>
                <p>
                  J'ajoute 5€ de parts sociales et j'obtiens plus de
                  contreparties et davantages fiscaux.
                </p>
                <Link
                  to={() => ({
                    pathname: "/soutien/socio/coordonnees",
                    state: getValues(),
                  })}
                >
                  Je deviens Socio !
                </Link>
              </SwitchPath>
            )}

            {isSocioPath && (
              <>
                <SmallFields>
                  <fieldset>
                    <Controller
                      as={Select}
                      name="gender"
                      label="Sexe"
                      control={control}
                      rules={{
                        required: "Le sexe est requis.",
                      }}
                      required
                      error={errors.gender !== undefined}
                      helperText={errors.gender && errors.gender.message}
                    >
                      <MenuItem value="F">Féminin</MenuItem>
                      <MenuItem value="M">Masculin</MenuItem>
                    </Controller>
                  </fieldset>
                  <fieldset>
                    <Controller
                      as={BirthdayPicker}
                      name="birthday"
                      control={control}
                      required
                      rules={{
                        required: "La date de naissance est requise.",
                        validate: (value) =>
                          differenceInYears(new Date(), new Date(value)) >= 18,
                      }}
                      error={errors.birthday !== undefined}
                      helperText={
                        errors.birthday &&
                        (errors.birthday.message ||
                          (errors.birthday.type === "validate" &&
                            "Vous devez être majeur."))
                      }
                    />
                  </fieldset>
                </SmallFields>
                <SmallFields>
                  <fieldset>
                    <Controller
                      as={TextField}
                      label="Ville de naissance"
                      name="birth_locality"
                      control={control}
                      required
                      rules={{ required: "Le lieu de naissance est requis." }}
                      error={errors.birth_locality !== undefined}
                      helperText={
                        errors.birth_locality && errors.birth_locality.message
                      }
                    />
                  </fieldset>
                  <fieldset>
                    <Controller
                      as={TextField}
                      label="Pays de naissance"
                      name="birth_country_name"
                      control={control}
                      required
                      rules={{ required: "Le pays de naissance est requis." }}
                      error={errors.birth_country_name !== undefined}
                      helperText={
                        errors.birth_country_name &&
                        errors.birth_country_name.message
                      }
                    />
                  </fieldset>
                </SmallFields>
                <SmallFields>
                  <fieldset>
                    <Controller
                      as={TextField}
                      label="Numéro de téléphone"
                      name="phone"
                      type="tel"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/,
                          message:
                            "Le format du numéro de téléphone est invalide.",
                        },
                      }}
                      error={errors.phone !== undefined}
                      helperText={errors.phone && errors.phone.message}
                    />
                  </fieldset>
                </SmallFields>
              </>
            )}
            {account.error && (
              <p className="error" style={{ marginTop: "30px" }}>
                {account.error}
              </p>
            )}
            <Button type="submit">Enregistrer mes coordonnées</Button>
          </form>
        </SupportMain>
        <SupportAside>
          {isSocioPath ? <SocioOffer /> : <SubscriptionOffer />}
        </SupportAside>
      </SupportContent>
      <GridWrapper style={{ textAlign: "left" }}>
        <FAQ />
      </GridWrapper>
    </SupportContainer>
  );
}

SupportInformations.propTypes = {
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({
    state: PropTypes.object,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      path: PropTypes.oneOf(["abonnement", "socio"]).isRequired,
    }).isRequired,
  }).isRequired,
  account: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.string,
    address_billing: PropTypes.object,
  }).isRequired,
};

export default SupportInformations;
